import * as React from "react";
import { useTheme } from "@mui/material/styles";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LoadingButton } from "@mui/lab";
import {
  getWheelSizeMakes,
  getWheelSizeModels,
  getWheelSizeYears,
  getWheelSizeModifications,
  getWheelSizeFitmentSizes,
  WheelSizeOption,
  WheelSizeFitmentSize,
} from "../../../services/core-api";

type TireSearchProps = {
  compact?: boolean;
  hero?: boolean;
};

const TireSearch = ({ compact = false, hero = false }: TireSearchProps) => {
  const theme = useTheme();
  const [makes, setMakes] = React.useState<WheelSizeOption[]>([]);
  const [models, setModels] = React.useState<WheelSizeOption[]>([]);
  const [years, setYears] = React.useState<WheelSizeOption[]>([]);
  const [modifications, setModifications] = React.useState<WheelSizeOption[]>([]);

  const [selectedMake, setSelectedMake] = React.useState<WheelSizeOption | null>(null);
  const [selectedModel, setSelectedModel] = React.useState<WheelSizeOption | null>(null);
  const [selectedYear, setSelectedYear] = React.useState<WheelSizeOption | null>(null);
  const [selectedModification, setSelectedModification] =
    React.useState<WheelSizeOption | null>(null);

  const [loadingMakes, setLoadingMakes] = React.useState(false);
  const [loadingModels, setLoadingModels] = React.useState(false);
  const [loadingYears, setLoadingYears] = React.useState(false);
  const [loadingMods, setLoadingMods] = React.useState(false);
  const [isLoadingTire, setIsLoadingTire] = React.useState(false);
  const [configError, setConfigError] = React.useState<string | null>(null);
  const [fitmentModalOpen, setFitmentModalOpen] = React.useState(false);
  const [fitmentSizes, setFitmentSizes] = React.useState<WheelSizeFitmentSize[]>(
    []
  );
  const [fitmentError, setFitmentError] = React.useState<string | null>(null);

  const isStacked = compact || hero;

  const fieldSx = {
    flex: 1,
    width: "100%",
    minWidth: isStacked && !hero ? "100%" : 140,
    backgroundColor: "#fff",
    ".MuiInputBase-input": {
      height: hero ? "28px" : "40px",
      marginTop: "0px",
      marginBottom: "0px",
      paddingTop: "0px",
      paddingBottom: "0px",
    },
    ...(hero && {
      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
      "& .MuiInputBase-root": { padding: 0 },
      "& .MuiAutocomplete-popupIndicator": { color: "#9e9e9e" },
    }),
  };

  const labelProps = {
    sx: {
      color: theme.palette.text.primary,
      fontSize: "13px",
      height: "100%",
      top: "-4px",
    },
  };

  const heroFieldLabelSx = {
    display: "block",
    textAlign: "left" as const,
    color: "#9e9e9e",
    fontSize: "12px",
    fontWeight: 500,
    mb: 0.5,
    textTransform: "capitalize" as const,
  };

  const heroFieldCellSx = {
    flex: 1,
    minWidth: { xs: "100%", md: 0 },
    px: { xs: 2, md: 2.5 },
    py: { xs: 1.5, md: 2 },
    borderRight: { xs: "none", md: "1px solid #e8e8e8" },
    borderBottom: { xs: "1px solid #e8e8e8", md: "none" },
    "&:last-of-type": {
      borderRight: "none",
      borderBottom: { xs: "none", md: "none" },
    },
  };

  const renderHeroField = (
    label: string,
    field: React.ReactNode,
    options?: { hideDivider?: boolean }
  ) => (
    <Box
      sx={{
        ...heroFieldCellSx,
        ...(options?.hideDivider && {
          borderRight: "none",
          borderBottom: "none",
        }),
      }}
    >
      <Typography component="span" sx={heroFieldLabelSx}>
        {label}
      </Typography>
      {field}
    </Box>
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMakes(true);
      setConfigError(null);
      try {
        const data = await getWheelSizeMakes();
        if (!cancelled) setMakes(data);
      } catch (error: any) {
        const detail =
          error?.response?.data?.detail ||
          "Wheel-size API is not configured or unavailable.";
        if (!cancelled) setConfigError(String(detail));
      } finally {
        if (!cancelled) setLoadingMakes(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    setSelectedModel(null);
    setSelectedYear(null);
    setSelectedModification(null);
    setModels([]);
    setYears([]);
    setModifications([]);
    if (!selectedMake) return;
    let cancelled = false;
    (async () => {
      setLoadingModels(true);
      try {
        const data = await getWheelSizeModels(selectedMake.slug);
        if (!cancelled) setModels(data);
      } catch (error: any) {
        if (!cancelled) {
          setConfigError(
            String(error?.response?.data?.detail || "Failed to load models")
          );
        }
      } finally {
        if (!cancelled) setLoadingModels(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedMake]);

  React.useEffect(() => {
    setSelectedYear(null);
    setSelectedModification(null);
    setYears([]);
    setModifications([]);
    if (!selectedMake || !selectedModel) return;
    let cancelled = false;
    (async () => {
      setLoadingYears(true);
      try {
        const data = await getWheelSizeYears(selectedMake.slug, selectedModel.slug);
        if (!cancelled) setYears(data);
      } catch (error: any) {
        if (!cancelled) {
          setConfigError(
            String(error?.response?.data?.detail || "Failed to load years")
          );
        }
      } finally {
        if (!cancelled) setLoadingYears(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedMake, selectedModel]);

  React.useEffect(() => {
    setSelectedModification(null);
    setModifications([]);
    if (!selectedMake || !selectedModel || !selectedYear) return;
    let cancelled = false;
    (async () => {
      setLoadingMods(true);
      try {
        const data = await getWheelSizeModifications(
          selectedMake.slug,
          selectedModel.slug,
          selectedYear.slug
        );
        if (!cancelled) setModifications(data);
      } catch (error: any) {
        if (!cancelled) {
          setConfigError(
            String(
              error?.response?.data?.detail || "Failed to load modifications"
            )
          );
        }
      } finally {
        if (!cancelled) setLoadingMods(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedMake, selectedModel, selectedYear]);

  const handleSubmitTireData = async () => {
    if (!selectedMake || !selectedModel || !selectedYear) return;
    setIsLoadingTire(true);
    setFitmentError(null);
    try {
      const response = await getWheelSizeFitmentSizes({
        make: selectedMake.slug,
        model: selectedModel.slug,
        year: selectedYear.slug,
        modification: selectedModification?.slug,
      });
      const sizes = (response?.fitment_sizes || []).filter((s) => s?.raw);
      if (!sizes.length) {
        setFitmentError("No fitment sizes found for this vehicle.");
        return;
      }
      setFitmentSizes(sizes);
      setFitmentModalOpen(true);
    } catch (error: any) {
      setFitmentError(
        String(
          error?.response?.data?.detail ||
            "Failed to load fitment sizes from Wheel-size."
        )
      );
    } finally {
      setIsLoadingTire(false);
    }
  };

  const handleSelectFitment = (raw: string) => {
    if (!raw) return;
    setFitmentModalOpen(false);
    const params = [`fitment=${encodeURIComponent(raw)}`];
    if (selectedMake) params.push(`make=${encodeURIComponent(selectedMake.name || selectedMake.slug)}`);
    if (selectedModel) params.push(`model=${encodeURIComponent(selectedModel.name || selectedModel.slug)}`);
    if (selectedYear) params.push(`year=${encodeURIComponent(selectedYear.name || selectedYear.slug)}`);
    if (selectedModification) {
      params.push(
        `modification=${encodeURIComponent(
          selectedModification.name || selectedModification.slug
        )}`
      );
    }
    window.location.href = `/search-tyres?${params.join("&")}`;
  };

  const canSearch = Boolean(selectedMake && selectedModel && selectedYear);

  const makeField = (
    <Autocomplete
      options={makes}
      loading={loadingMakes}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.slug === b.slug}
      value={selectedMake}
      onChange={(_e, value) => setSelectedMake(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={hero ? undefined : "Make"}
          placeholder={hero ? "Select Make" : undefined}
          InputLabelProps={{ ...params.InputLabelProps, ...labelProps }}
          InputProps={{
            ...params.InputProps,
            sx: { borderRadius: 0, height: hero ? "32px" : "40px" },
          }}
        />
      )}
      sx={fieldSx}
    />
  );

  const modelField = (
    <Autocomplete
      options={models}
      loading={loadingModels}
      disabled={!selectedMake}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.slug === b.slug}
      value={selectedModel}
      onChange={(_e, value) => setSelectedModel(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={hero ? undefined : "Model"}
          placeholder={hero ? "Select Model" : undefined}
          InputLabelProps={{ ...params.InputLabelProps, ...labelProps }}
          InputProps={{
            ...params.InputProps,
            sx: { borderRadius: 0, height: hero ? "32px" : "40px" },
          }}
        />
      )}
      sx={fieldSx}
    />
  );

  const yearField = (
    <Autocomplete
      options={years}
      loading={loadingYears}
      disabled={!selectedModel}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.slug === b.slug}
      value={selectedYear}
      onChange={(_e, value) => setSelectedYear(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={hero ? undefined : "Year"}
          placeholder={hero ? "Select Year" : undefined}
          InputLabelProps={{ ...params.InputLabelProps, ...labelProps }}
          InputProps={{
            ...params.InputProps,
            sx: { borderRadius: 0, height: hero ? "32px" : "40px" },
          }}
        />
      )}
      sx={fieldSx}
    />
  );

  const modificationField = (
    <Autocomplete
      options={modifications}
      loading={loadingMods}
      disabled={!selectedYear}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.slug === b.slug}
      value={selectedModification}
      onChange={(_e, value) => setSelectedModification(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={hero ? undefined : "Modification"}
          placeholder={hero ? "Select Modification" : undefined}
          InputLabelProps={{ ...params.InputLabelProps, ...labelProps }}
          InputProps={{
            ...params.InputProps,
            sx: { borderRadius: 0, height: hero ? "32px" : "40px" },
          }}
        />
      )}
      sx={fieldSx}
    />
  );

  const searchButton = isLoadingTire ? (
    <LoadingButton
      loading
      variant="contained"
      sx={{
        height: hero ? { xs: "48px", md: "auto" } : "38px",
        width: hero ? { xs: "100%", md: "140px" } : "100%",
        minHeight: hero ? { xs: "48px", md: "72px" } : undefined,
        alignSelf: hero ? "stretch" : undefined,
        borderRadius: 0,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "secondary.main",
        color: "text.secondary",
        ":hover": {
          backgroundColor: "secondary.main",
        },
      }}
    >
      Search
    </LoadingButton>
  ) : (
    <Button
      disabled={!canSearch || Boolean(configError)}
      sx={{
        height: hero ? { xs: "48px", md: "auto" } : "38px",
        width: hero ? { xs: "100%", md: "140px" } : "100%",
        minHeight: hero ? { xs: "48px", md: "72px" } : undefined,
        alignSelf: hero ? "stretch" : undefined,
        textTransform: "capitalize",
        borderRadius: 0,
        backgroundColor: "secondary.main",
        color: "text.secondary",
        ":hover": {
          backgroundColor: "secondary.main",
        },
      }}
      onClick={handleSubmitTireData}
    >
      Search
    </Button>
  );

  const fitmentDialog = (
    <Dialog
      open={fitmentModalOpen}
      onClose={() => setFitmentModalOpen(false)}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Select tire size</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Choose a fitment size to search Financa5 stock.
        </Typography>
        <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {fitmentSizes.map((size) => (
            <ListItemButton
              key={size.raw}
              onClick={() => handleSelectFitment(size.raw)}
              sx={{
                cursor: "pointer",
                borderRadius: 1,
                border: "1px solid #d0d0d0",
                backgroundColor: "#fff",
                px: 2,
                py: 1.25,
                transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
                "&:hover": {
                  backgroundColor: "#f7f7f7",
                  borderColor: theme.palette.secondary.main,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                },
                "&:active": {
                  backgroundColor: "#f0f0f0",
                },
              }}
            >
              <ListItemText
                primary={size.raw}
                primaryTypographyProps={{
                  fontWeight: 600,
                  fontFamily: "Inter",
                  fontSize: "15px",
                  color: theme.palette.primary.main,
                }}
              />
              <ListItemIcon sx={{ minWidth: 28, justifyContent: "flex-end" }}>
                <ChevronRightIcon
                  sx={{ color: theme.palette.secondary.main, fontSize: 22 }}
                />
              </ListItemIcon>
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setFitmentModalOpen(false)}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (hero) {
    return (
      <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
        {configError && (
          <Alert
            severity="warning"
            sx={{ mb: 2, textAlign: "left", maxWidth: 900, mx: "auto" }}
          >
            {configError}
          </Alert>
        )}
        {fitmentError && (
          <Alert
            severity="warning"
            sx={{ mb: 2, textAlign: "left", maxWidth: 900, mx: "auto" }}
          >
            {fitmentError}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            width: "100%",
            maxWidth: 1100,
            mx: "auto",
            backgroundColor: "#fff",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
            overflow: "hidden",
          }}
        >
          {renderHeroField("Make", makeField)}
          {renderHeroField("Model", modelField)}
          {renderHeroField("Year", yearField)}
          {renderHeroField("Modification", modificationField, {
            hideDivider: true,
          })}
          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              alignSelf: "stretch",
              width: { xs: "100%", md: "auto" },
              flexShrink: 0,
              px: { xs: 2, md: 0 },
              py: { xs: 2, md: 0 },
            }}
          >
            {searchButton}
          </Box>
        </Box>
        {fitmentDialog}
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", zIndex: 1, textAlign: "left" }}>
      <Typography
        variant="body1"
        align="left"
        sx={{
          fontSize: "13px",
          color: theme.palette.text.primary,
          m: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        Search tires by vehicle (Make → Model → Year → Modification)
      </Typography>

      {configError && (
        <Alert severity="warning" sx={{ m: 1, textAlign: "left" }}>
          {configError}
        </Alert>
      )}
      {fitmentError && (
        <Alert severity="warning" sx={{ m: 1, textAlign: "left" }}>
          {fitmentError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: compact ? "column" : "row",
          flexWrap: "wrap",
          gap: 1,
          width: "100%",
          px: 1,
        }}
      >
        {makeField}
        {modelField}
        {yearField}
        {modificationField}
      </Box>

      <Box sx={{ marginTop: 1, px: 1 }}>{searchButton}</Box>
      {fitmentDialog}
    </Box>
  );
};

export default TireSearch;
